/** Client-side WSOLA time stretching. Never resample/change playbackRate to
 * alter speed: those would shift pitch. Original/cache PCM stays untouched. */
export const MIN_AUDIO_SPEED=.5,MAX_AUDIO_SPEED=2,AUDIO_SPEED_STEP=.01;
export interface AudioSpeedDirection {audioSpeed?:number}
const failure=(message:string)=>Object.assign(new Error(message),{retryable:false,code:'LOCAL_AUDIO_PROCESSING'});
export function actualSpeed(direction?:AudioSpeedDirection):number|undefined {
  const value=direction?.audioSpeed;
  if(value===undefined)return undefined;
  if(typeof value!=='number'||!Number.isFinite(value)||value<MIN_AUDIO_SPEED||value>MAX_AUDIO_SPEED||Math.abs(value*100-Math.round(value*100))>1e-7)throw failure('Choose recording speed between 0.50× and 2.00× in 0.01× steps.');
  return value===1?undefined:value;
}
function check(signal?:AbortSignal){if(signal?.aborted)throw Object.assign(failure('Audio adjustment stopped.'),{name:'AbortError'});}
/** Overlap-add with a waveform-similarity search, shared alignment for channels.
 * Yield regularly so Stop/cancel stays responsive, including during MP3 export.
 * Speech-sized frames; exact output length, first and final frames anchored. */
export async function processAudioSpeed(buffer:AudioBuffer,speed?:number,signal?:AbortSignal):Promise<AudioBuffer>{
  check(signal);const rate=actualSpeed({audioSpeed:speed});if(!rate)return buffer;
  const {sampleRate,length,numberOfChannels}=buffer;
  const target=Math.round(length/rate);
  if(!Number.isFinite(sampleRate)||sampleRate<8000||sampleRate>192000||!Number.isInteger(length)||length<sampleRate*.04||!Number.isInteger(numberOfChannels)||numberOfChannels<1||numberOfChannels>8||target*numberOfChannels>16_000_000)throw failure('This recording cannot be safely time-stretched in this browser. Reset recording speed; the original recording is unchanged.');
  let output:AudioBuffer;
  try{output=new AudioBuffer({length:target,numberOfChannels,sampleRate});}
  catch{throw failure('This browser could not allocate the speed-adjusted audio. Reset recording speed; the original recording is unchanged.');}
  const input=Array.from({length:numberOfChannels},(_,c)=>buffer.getChannelData(c));
  const result=Array.from({length:numberOfChannels},(_,c)=>output.getChannelData(c));
  const hop=Math.max(1,Math.min(Math.round(sampleRate*.02),Math.floor(length/8))),window=2*hop;
  const last=target-window,sourceLast=length-window,search=Math.round(sampleRate*.015);
  const finite=(value:number)=>Number.isFinite(value)?value:0;
  // Prefer the channel carrying signal (a stereo reference can have a silent side).
  let matchChannel=0,energy=-1;
  for(let c=0;c<numberOfChannels;c++){let sum=0;for(let i=0;i<length;i+=64){const v=finite(input[c][i]);sum+=v*v;}if(sum>energy){energy=sum;matchChannel=c;}}
  for(let c=0;c<numberOfChannels;c++)for(let i=0;i<window;i++)result[c][i]=finite(input[c][i]);
  let previousEnd=window,previousInput=0,frames=0;
  for(let out=Math.min(hop,last);out<=last;out=Math.min(out+hop,last)){
    check(signal);
    const overlap=previousEnd-out,nominal=Math.round(out*sourceLast/last);
    const reference=result[matchChannel],source=input[matchChannel];
    const score=(position:number)=>{let dot=0,a=0,b=0;
      for(let i=0;i<overlap;i+=4){const x=reference[out+i],y=finite(source[position+i]);dot+=x*y;a+=x*x;b+=y*y;}
      return a*b>1e-16?dot/Math.sqrt(a*b):-1;
    };
    let chosen=nominal,best=score(nominal);
    if(out!==last){
      const lo=Math.max(previousInput,nominal-search),hi=Math.min(sourceLast,nominal+search);
      for(let candidate=lo;candidate<=hi;candidate+=4){const value=score(candidate);if(value>best+1e-8){best=value;chosen=candidate;}}
      const coarse=chosen;
      for(let candidate=Math.max(lo,coarse-3);candidate<=Math.min(hi,coarse+3);candidate++){const value=score(candidate);if(value>best+1e-8){best=value;chosen=candidate;}}
    }
    for(let c=0;c<numberOfChannels;c++)for(let i=0;i<window;i++){
      const sample=finite(input[c][chosen+i]);
      result[c][out+i]=i<overlap?result[c][out+i]*(1-i/overlap)+sample*(i/overlap):sample;
    }
    previousEnd=out+window;previousInput=chosen;
    if(out===last)break;
    if(++frames%8===0){await new Promise<void>(resolve=>setTimeout(resolve,0));check(signal);}
  }
  check(signal);return output;
}
